import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { EquipmentPositionSummary } from '../../interfaces/MachinePosition.types';
import { MachinePositionHistoryMapPin } from '../MachinePositionHistoryMapPin/MachinePositionHistoryMapPin';
import { MachinePositionHistoryMapPinProps } from '../MachinePositionHistoryMapPin/MachinePositionHistoryMapPin.types';
import { MACHINE_POSITION_HISTORY_MAP_CONSTANTS } from './MachinePositionHistoryMap.constants';
import { MachinePositionHistoryMapGlobalStyles } from './MachinePositionHistoryMap.global.styles';
import { MachinePositionHistoryMapWrapper } from './MachinePositionHistoryMap.styles';
import { VisGLMap } from 'app/components/VisGLMap/VisGLMap';
import { useMapInitialZoom } from 'app/hooks/useMapInitialZoom';

interface MachinePositionHistoryMapProps {
  machinePositionSummaries: EquipmentPositionSummary[];
  isLoading: boolean;
}

export const MachinePositionHistoryMap = ({
  machinePositionSummaries,
  isLoading,
}: MachinePositionHistoryMapProps): JSX.Element => {
  const map = useMap(MACHINE_POSITION_HISTORY_MAP_CONSTANTS.mapInstanceId);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const [activePinId, setActivePinId] = useState<string | null>(null);

  const handleClick = useCallback(
    (newActivePinId: string): void => {
      if (activePinId === newActivePinId) {
        setActivePinId(null);
        return;
      }

      setActivePinId(newActivePinId);
    },
    [activePinId]
  );

  const closeAllPins = useCallback((): void => setActivePinId(null), []);

  const machinePositionHistoryMapPins = useMemo((): (MachinePositionHistoryMapPinProps & {
    lat: number;
    lng: number;
  })[] => {
    const pinsByCoordinates = new globalThis.Map<
      string,
      MachinePositionHistoryMapPinProps & { lat: number; lng: number }
    >();

    machinePositionSummaries.forEach((positionSummary, idx) => {
      const pinKey = `${positionSummary.latitude},${positionSummary.longitude}`;

      if (pinsByCoordinates.has(pinKey)) {
        pinsByCoordinates.get(pinKey)?.equipmentPositionSummaries.push(positionSummary);
      } else {
        const newPin = {
          id: pinKey,
          equipmentPositionSummaries: [positionSummary],
          lat: positionSummary.latitude,
          lng: positionSummary.longitude,
          visible: pinKey === activePinId,
          onClick: handleClick,
          numberOfOrder: idx + 1,
          isClicked: activePinId === pinKey,
        };
        pinsByCoordinates.set(pinKey, newPin);
      }
    });

    return Array.from(pinsByCoordinates.values()).sort((mapPin1, mapPin2) => mapPin2.lat - mapPin1.lat);
  }, [activePinId, handleClick, machinePositionSummaries]);

  useMapInitialZoom({
    map,
    padding: 100,
    mapElements: machinePositionHistoryMapPins.map(({ lat, lng }) => ({
      type: 'pin' as const,
      center: { lat, lng },
    })),
    shouldZoom: !isLoading,
  });

  useEffect(
    () => () => {
      if (!map) return;
      map.setZoom(0);
    },
    [map]
  );

  return (
    <>
      <MachinePositionHistoryMapGlobalStyles />
      <MachinePositionHistoryMapWrapper>
        <VisGLMap
          id={MACHINE_POSITION_HISTORY_MAP_CONSTANTS.mapInstanceId}
          ref={mapRef}
          onDragstart={closeAllPins}
          onZoomChanged={closeAllPins}
          isLoading={isLoading}
        >
          {isLoading
            ? null
            : machinePositionHistoryMapPins.map((pinProps, index) => (
                <AdvancedMarker
                  key={`${pinProps.id}-${index.toString()}`}
                  position={{ lat: pinProps.lat, lng: pinProps.lng }}
                  clickable
                >
                  <MachinePositionHistoryMapPin popupContainerRef={mapRef.current ?? undefined} {...pinProps} />
                </AdvancedMarker>
              ))}
        </VisGLMap>
      </MachinePositionHistoryMapWrapper>
    </>
  );
};
